import { FC, ReactNode } from "react";
import { ChangeNames, ChangeTypes } from "../../clients/FirebaseClient/types";
import { useFormModel } from "../core/FormModelHelpes";
import {
    makeStyles,
    createStyles,
    FormControl,
    Grid,
    useTheme,
    useMediaQuery,
} from "@material-ui/core";
import { Checkbox } from "../core/Checkbox";
import { FormControlLabel } from "../core/FormControlLabel";
import { ChargingDeviceIcon, HeatPumpIcon, RoomCoolingIcon } from "../core/icons/Icons";
import { useStyles } from "../../useStyles";
import { InfoBox } from "../core/InfoBox";

const useFormControlStyles = makeStyles(() =>
    createStyles({
        topText: {
            margin: "16px 0 12px",
            marginTop: "96px",
            marginBottom: "24px",
        },
        container: {
            display: "flex",
            position: "relative",
            flexDirection: "column",
            height: "100%",
            borderRadius: "12px",
            padding: "18px",
            boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.1)",
        },
        containerSmall: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            height: "100%",
            minHeight: "96px",
            borderRadius: "12px",
            padding: "24px 24px 24px 24px",
            boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.1)",
        },
        icon: {
            width: "72px",
            height: "72px",
            alignSelf: "center",
            padding: "5px 4px 4px 5px",
            borderRadius: "8px",
            objectFit: "contain",
        },
        iconSmall: {
            width: "56px",
            height: "56px",
            borderRadius: "8px",
            objectFit: "contain",
        },
        formControl: {
            fontFamily: "innogyWEB",
            fontSize: "22px",
            fontWeight: 300,
            lineHeight: 1.27,
            color: "#3c3732",
            flexGrow: 1,
        },
        formControlSmall: {
            fontFamily: "innogyWEB",
            marginTop: "0px",
            fontSize: "22px",
            fontWeight: 300,
            lineHeight: 1.27,
            color: "#3c3732",
        },
        formControlContainer: {
            width: "100%",
        }
    }),
);

interface AdjustableLoadTypesTileProps {
    icon: ReactNode;
    value: keyof ChangeTypes[ChangeNames.ADJUSTABLE_LOAD_TYPES]["value"];
    onChange: (checked: boolean) => void;
    label: string;
    error: boolean;
    size?: "default" | "double";
}
const AdjustableLoadTypesTileBig: FC<AdjustableLoadTypesTileProps> = (
    props: AdjustableLoadTypesTileProps,
) => {
    const formControlStyles = useFormControlStyles();
    const { values } = useFormModel<ChangeNames.ADJUSTABLE_LOAD_TYPES>();
    return (
        <div
            className={formControlStyles.container}
            style={{
                backgroundColor: values.adjustableLoadTypes.value[props.value] ? "white" : "#f8f8f8",
                border: "solid 1px #dddddd",
            }}
        >
            <div className={formControlStyles.icon}>{props.icon}</div>
            <FormControlLabel
                label={props.label}
                className={formControlStyles.formControl}
                isCheckBox={true}
                control={
                    <Checkbox
                        value={props.value}
                        checked={values.adjustableLoadTypes.value[props.value]}
                        onChange={(_, checked) => {
                            props.onChange(checked);
                        }}
                    />
                }
            />
        </div>
    );
};
const AdjustableLoadTypesTileSmall: FC<AdjustableLoadTypesTileProps> = (
    props: AdjustableLoadTypesTileProps,
) => {
    const formControlStyles = useFormControlStyles();
    const { values } = useFormModel<ChangeNames.ADJUSTABLE_LOAD_TYPES>();
    return (
        <Grid item>
            <div
                className={formControlStyles.containerSmall}
                style={{
                    backgroundColor: values.adjustableLoadTypes.value[props.value]
                        ? "white"
                        : "#f8f8f8",
                    border: "solid 1px #dddddd",
                }}
            >
                <FormControlLabel
                    label={props.label}
                    className={formControlStyles.formControlSmall}
                    isCheckBox={true}
                    control={
                        <Checkbox
                            value={props.value}
                            checked={values.adjustableLoadTypes.value[props.value]}
                            onChange={(_, checked) => {
                                props.onChange(checked);
                            }}
                        />
                    }
                />
                <div className={formControlStyles.iconSmall}>{props.icon}</div>
            </div>
        </Grid>
    );
};

const AdjustableLoadTypesTile: FC<AdjustableLoadTypesTileProps> = (
    props: AdjustableLoadTypesTileProps,
) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
    return (
        <Grid item xs={12} lg={props.size === "double" ? 8 : 4}>
            {isSmallScreen ? (
                <AdjustableLoadTypesTileSmall {...props} />
            ) : (
                <AdjustableLoadTypesTileBig {...props} />
            )}
        </Grid>
    );
};

export const AdjustableLoadTypesForm: FC = () => {
    const { values, setValues } = useFormModel<ChangeNames.ADJUSTABLE_LOAD_TYPES>();
    const formClasses = useFormControlStyles();
    const classes = useStyles();

    return (
        <FormControl component="fieldset" className={`${formClasses.formControlContainer}`}>
          <h4 className={`${classes.h4} ${formClasses.topText}`}>
            Welche Verbrauchsgeräte möchten Sie anmelden?
          </h4>
            <InfoBox title="Hinweis" style={{ marginTop: 0 }}>
              <p>
              Gemäß § 14a EnWG müssen Ladeeinrichtungen, Wärmepumpen, Geräte zur Raumkühlung und
              Bezugsspeicher unter bestimmten Voraussetzungen steuerbar sein.
              Weitere Informationen finden Sie auf unserer{" "}
              <a href="https://www.syna.de/corp/steuerbare-verbrauchseinrichtungen">
              Webseite
              </a>.
              </p>
            </InfoBox>
            <Grid container spacing={2}>
                <AdjustableLoadTypesTile
                    value="charger"
                    onChange={(checked) => {
                        setValues((values) => {
                            values.adjustableLoadTypes.value.charger = checked;
                        });
                    }}
                    label="Ladeeinrichtung"
                    error={values.adjustableLoadTypes.error !== null}
                    icon={<ChargingDeviceIcon size="size56" />}
                />
                <AdjustableLoadTypesTile
                    value="heatPump"
                    onChange={(checked) => {
                        setValues((values) => {
                            values.adjustableLoadTypes.value.heatPump = checked;
                        });
                    }}
                    label="Wärmepumpe"
                    error={values.adjustableLoadTypes.error !== null}
                    icon={<HeatPumpIcon size="size56" />}
                />
                <AdjustableLoadTypesTile
                    value="roomCoolingDevice"
                    onChange={(checked) => {
                        setValues((values) => {
                            values.adjustableLoadTypes.value.roomCoolingDevice = checked;
                        });
                    }}
                    label="Gerät zur Raumkühlung"
                    error={values.adjustableLoadTypes.error !== null}
                    icon={<RoomCoolingIcon size="size56" />}
                />
            </Grid>
        </FormControl>
    );
};
